import React, { useState, useEffect, useRef } from 'react';
import { Form, Row, Col, Button, Alert } from 'react-bootstrap';
import Select from 'react-select';
import './PlayerForm.css';
import imageCompression from 'browser-image-compression';
import secureApi from '../utils/secureApi';
import { Player, PlayerFormData, PlayerValidationErrors } from '../types/Player';
import { PLAYER_STATUS_CONFIG } from '../types/Player';
import { getTournamentById, getCurrentActiveTournament, testTournamentAPI, ManageTournament } from '../api/tournament/manageTournamentService';
import { getImageUrl } from '../utils/imageUrl';

// Make test function available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).testTournamentAPI = testTournamentAPI;
}


interface PlayerFormProps {
  player?: Player | null;
  teamId: string;
  teamName: string;
  ageCategories: string[];
  teams?: Array<{ value: string; label: string; ageCategory?: string }>;
  showTeamField?: boolean; // New prop to control team field visibility
  onTeamChange?: (teamId: string) => void; // New prop for team selection callback
  onSubmit: (playerData: PlayerFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  submitError: string | null;
  isReadOnly?: boolean; // New prop for read-only mode
  teamAgeCategories?: string[]; // New prop for team's age categories (array)
  hideAgeCategoryField?: boolean; // New prop to hide age category field
  requireDocuments?: boolean; // New prop to make documents required (for admin context)
  tournamentId?: string; // New prop for tournament ID to fetch DOB limits
  tournamentSport?: string; // New prop for tournament sport (required for tournament management module)
}

const PlayerForm: React.FC<PlayerFormProps> = ({
  player,
  teamId,
  teamName,
  ageCategories,
  teams = [],
  showTeamField = false, // Default to false (hide team field)
  onTeamChange,
  onSubmit,
  onCancel,
  isSubmitting,
  submitError,
  isReadOnly = false,
  teamAgeCategories,
  hideAgeCategoryField = false,
  requireDocuments = false,
  tournamentId,
  tournamentSport
}) => {
  console.log('🎯 PlayerForm received ageCategories:', ageCategories);
  console.log('🎯 PlayerForm showTeamField:', showTeamField);
  console.log('🎯 PlayerForm ageCategories length:', ageCategories.length);
  console.log('🎯 PlayerForm ageCategories data:', ageCategories);
  const [formData, setFormData] = useState<PlayerFormData>({
    name: '',
    profilePic: '',
    dob: '',
    ageCategory: '',
    parentName: '',
    documents: []
  });

  // Add state to track original form data for change detection
  const [originalFormData, setOriginalFormData] = useState<PlayerFormData>({
    name: '',
    profilePic: '',
    dob: '',
    ageCategory: '',
    parentName: '',
    documents: []
  });

  const [validationErrors, setValidationErrors] = useState<PlayerValidationErrors>({});
  const [isProfilePicUploading, setIsProfilePicUploading] = useState(false);
  const [profilePicUploadProgress, setProfilePicUploadProgress] = useState(0);
  const [profilePicError, setProfilePicError] = useState<string | null>(null);
  const [profilePicSuccess, setProfilePicSuccess] = useState(false);

  // Document upload states
  const [isIdProofUploading, setIsIdProofUploading] = useState(false);
  const [idProofUploadProgress, setIdProofUploadProgress] = useState(0);
  const [idProofError, setIdProofError] = useState<string | null>(null);
  const [idProofSuccess, setIdProofSuccess] = useState(false);

  const [isConsentFormUploading, setIsConsentFormUploading] = useState(false);
  const [consentFormUploadProgress, setConsentFormUploadProgress] = useState(0);
  const [consentFormError, setConsentFormError] = useState<string | null>(null);
  const [consentFormSuccess, setConsentFormSuccess] = useState(false);
  
  // Tournament data for DOB validation
  const [tournamentData, setTournamentData] = useState<ManageTournament | null>(null);
  const [isTournamentLoading, setIsTournamentLoading] = useState(false);
  const [dobValidationMessage, setDobValidationMessage] = useState<string>('');
  


  const profilePicInputRef = useRef<HTMLInputElement>(null);
  const idProofInputRef = useRef<HTMLInputElement>(null);
  const consentFormInputRef = useRef<HTMLInputElement>(null);

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = (): string => {
    const today = new Date();
    return today.toISOString().slice(0, 10);
  };

  // Helper function to get DOB date limits based on selected age category
  const getDobDateLimits = (): { min?: string; max?: string } => {
    const limits: { min?: string; max?: string } = {};
    
    // Always set max to today's date to prevent future dates
    limits.max = getTodayDate();
    
    // Set min date based on tournament age category if available
    if (tournamentData && formData.ageCategory) {
      const ageCategoryPricing = tournamentData.pricing?.find(
        p => p.ageCategory === formData.ageCategory
      );
      
      if (ageCategoryPricing && ageCategoryPricing.dobLimit) {
        // dobLimit is the earliest allowed date, so set as min
        limits.min = ageCategoryPricing.dobLimit;
      }
    }
    
    return limits;
  };

  // Initialize form data when editing
  useEffect(() => {
    if (player) {
      console.log('🎯 Player data received in PlayerForm:', player);
      
      // Use player's original age category (no validation needed since field is hidden when editing)
      let playerAgeCategory = player.ageCategory || '';
      
      const newFormData = {
        name: player.name || '',
        profilePic: player.profilePic || '',
        dob: player.dob || '',
        ageCategory: playerAgeCategory,
        parentName: player.parentName || '',
        documents: player.documents || []
      };
      
      console.log('📝 Form data initialized:', newFormData);
      console.log('📝 Player documents in PlayerForm:', player.documents);
      console.log('📝 Form documents:', newFormData.documents);
      setFormData(newFormData);
      setOriginalFormData(newFormData); // Set original data for change detection
    } else {
      // Clear form data when adding new player
      const emptyFormData = {
        name: '',
        profilePic: '',
        dob: '',
        ageCategory: teamAgeCategories && teamAgeCategories.length > 0 ? teamAgeCategories[0] : '', // Prefill with first team age category if available
        parentName: '',
        documents: []
      };
      setFormData(emptyFormData);
      setOriginalFormData(emptyFormData);
    }
  }, [player, teamAgeCategories]);

  // Handle team selection changes to prefill age category
  useEffect(() => {
    // Only prefill for new players when team age categories change
    if (!player && teamAgeCategories && teamAgeCategories.length > 0) {
      // If age category is not set or if it's not in the team's age categories, update it
      if (!formData.ageCategory || !teamAgeCategories.includes(formData.ageCategory)) {
        console.log('🎯 Prefilling age category from team selection:', teamAgeCategories[0]);
        setFormData(prev => ({ 
          ...prev, 
          ageCategory: teamAgeCategories[0] 
        }));
      }
    }
  }, [teamAgeCategories, player]);

  // Fetch tournament data for DOB validation
  useEffect(() => {
    const fetchTournamentData = async () => {
      console.log('🏆 PlayerForm - Fetching tournament data...');
      console.log('🏆 PlayerForm - tournamentId prop:', tournamentId);
      
      try {
        setIsTournamentLoading(true);
        let tournament: ManageTournament | null = null;
        
        if (tournamentId) {
          console.log('🏆 PlayerForm - Using specific tournament ID:', tournamentId);
          // Get sport from prop, location state, or URL params
          // For tournament management module, sport is required
          const urlParams = new URLSearchParams(window.location.search);
          const sportFromUrl = urlParams.get('sport');
          const selectedTournamentFromState = (window.history.state?.usr as any)?.selectedTournament;
          const sportFromState = selectedTournamentFromState?.sports;
          const sport = tournamentSport || sportFromState || sportFromUrl || 'Football'; // Default to Football for DOB validation
          
          tournament = await getTournamentById(tournamentId, sport);
        } else {
          console.log('🏆 PlayerForm - Getting current active tournament...');
          tournament = await getCurrentActiveTournament();
        }
        
        console.log('🏆 PlayerForm - Tournament data received:', tournament);
        console.log('🏆 PlayerForm - Tournament pricing:', tournament?.pricing);
        setTournamentData(tournament);
      } catch (error) {
        console.error('❌ PlayerForm - Failed to fetch tournament data:', error);
        setTournamentData(null);
      } finally {
        setIsTournamentLoading(false);
      }
    };

    fetchTournamentData();
  }, [tournamentId]);

  // Update DOB validation message when age category changes
  useEffect(() => {
    console.log('📅 PlayerForm - DOB validation update triggered');
    console.log('📅 PlayerForm - tournamentData:', !!tournamentData);
    console.log('📅 PlayerForm - formData.ageCategory:', formData.ageCategory);
    
    if (!tournamentData || !formData.ageCategory) {
      console.log('📅 PlayerForm - Clearing DOB validation message (no tournament or age category)');
      setDobValidationMessage('');
      return;
    }

    const ageCategoryPricing = tournamentData.pricing?.find(
      p => p.ageCategory === formData.ageCategory
    );

    console.log('📅 PlayerForm - Found age category pricing:', ageCategoryPricing);

    if (ageCategoryPricing && ageCategoryPricing.dobLimit) {
      const dobLimitDate = new Date(ageCategoryPricing.dobLimit);
      const formattedDate = dobLimitDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      const message = `Date of birth limit is: ${formattedDate}`;
      console.log('📅 PlayerForm - Setting DOB validation message:', message);
      setDobValidationMessage(message);
    } else {
      console.log('📅 PlayerForm - No dobLimit found for age category');
      setDobValidationMessage('');
    }
  }, [tournamentData, formData.ageCategory]);

  // Reset success states when player changes
  useEffect(() => {
    setProfilePicSuccess(false);
    setIdProofSuccess(false);
    setConsentFormSuccess(false);
    setProfilePicError(null);
    setIdProofError(null);
    setConsentFormError(null);
  }, [player]);

  // Handle cancel with success state reset
  const handleCancel = () => {
    // Reset all success and error states
    setProfilePicSuccess(false);
    setIdProofSuccess(false);
    setConsentFormSuccess(false);
    setProfilePicError(null);
    setIdProofError(null);
    setConsentFormError(null);
    
    // Call the original onCancel function
    onCancel();
  };

  // Validation functions
  const validateName = (name: string): string | undefined => {
    if (!name.trim()) return 'Player name is required';
    if (name.trim().length < 2) return 'Player name must be at least 2 characters';
    return undefined;
  };

  const validateParentName = (parentName: string): string | undefined => {
    if (!parentName.trim()) return 'Parent name is required';
    if (parentName.trim().length < 2) return 'Parent name must be at least 2 characters';
    return undefined;
  };

  const validateDob = (dob: string): string | undefined => {
    if (!dob) return 'Date of birth is required';
    
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dob)) return 'Date must be in YYYY-MM-DD format';
    
    const date = new Date(dob);
    if (isNaN(date.getTime())) return 'Invalid date';
    
    const today = new Date();
    today.setHours(23, 59, 59, 999); // Set to end of today to allow today's date
    
    if (date > today) return 'Date of birth cannot be in the future';
    
    // Check if the person is at least 1 year old (reasonable minimum age)
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    if (date > oneYearAgo) return 'Date of birth must be at least 1 year ago';
    
    // Check if the person is not older than 120 years (reasonable maximum age)
    const maxAgeDate = new Date();
    maxAgeDate.setFullYear(maxAgeDate.getFullYear() - 120);
    if (date < maxAgeDate) return 'Please enter a valid date of birth';
    
    // Tournament-specific age category validation
    if (tournamentData && formData.ageCategory) {
      const ageCategoryPricing = tournamentData.pricing?.find(
        p => p.ageCategory === formData.ageCategory
      );
      
      if (ageCategoryPricing && ageCategoryPricing.dobLimit) {
        const dobLimitDate = new Date(ageCategoryPricing.dobLimit);
        if (date < dobLimitDate) {
          const formattedDate = dobLimitDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
          return `Date of birth must be on or after ${formattedDate} for ${formData.ageCategory}`;
        }
      }
    }
    
    return undefined;
  };

  const validateAgeCategory = (ageCategory: string): string | undefined => {
    if (!ageCategory) return 'Age category is required';
    return undefined;
  };

  const validateProfilePic = (profilePic: string): string | undefined => {
    // Profile picture is now mandatory
    if (!profilePic || profilePic.trim() === '') return 'Profile picture is required';
    return undefined;
  };

  const validateDocuments = (documents: any[]): string | undefined => {
    const hasIdProof = documents.some(doc => doc.type === 'id-proof');
    const hasConsentForm = documents.some(doc => doc.type === 'consent-form');
    
    if (!hasIdProof) return 'ID Proof document is required';
    if (!hasConsentForm) return 'Consent Form document is required';
    return undefined;
  };

  const validateForm = (): boolean => {
    const errors: PlayerValidationErrors = {};
    
    errors.name = validateName(formData.name);
    errors.parentName = validateParentName(formData.parentName);
    errors.dob = validateDob(formData.dob);
    errors.ageCategory = validateAgeCategory(formData.ageCategory);
    errors.profilePic = validateProfilePic(formData.profilePic);
    errors.documents = validateDocuments(formData.documents);
    
    setValidationErrors(errors);
    
    return !Object.values(errors).some(error => error !== undefined);
  };

  const handleFormChange = (field: keyof PlayerFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleProfilePicClick = () => {
    profilePicInputRef.current?.click();
  };

  const handleProfilePicUpload = async (file: File) => {
    // Validate file
    if (!file.type.startsWith('image/')) {
      setProfilePicError('Please select a valid image file.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setProfilePicError('Image size should be less than 5MB.');
      return;
    }

    setProfilePicError(null);
    setIsProfilePicUploading(true);
    setProfilePicUploadProgress(0);
    setProfilePicSuccess(false);

    try {
      // Compress the image
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true,
        onProgress: (progress) => setProfilePicUploadProgress(progress)
      });

      // Generate file name and context
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const ext = compressedFile.type.split('/')[1] || 'jpg';
      const fileName = `player_pic_${Date.now()}.${ext}`;
      const context = `my-club/player-profile/${dd}-${mm}-${yyyy}`;

      // Get presigned URL
      const presignRes = await secureApi.post(
        `/api/v1/general/presigned-url`,
        {
          fileName,
          contentType: compressedFile.type,
          context,
        }
      );

      const presignedUrl = presignRes.data?.presignedUrl;
      const s3Key = presignRes.data?.key;
      
      if (!presignedUrl || !s3Key) {
        throw new Error('Failed to get presigned URL');
      }

      // Upload to S3
      const uploadRes = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': compressedFile.type },
        body: compressedFile,
      });
      
      if (!uploadRes.ok) {
        throw new Error('Failed to upload image to S3');
      }

      // Update form data with new profile pic
      setFormData(prev => ({ ...prev, profilePic: s3Key }));
      setProfilePicSuccess(true);
      
      // Note: Change detection will automatically work since formData is updated
      
    } catch (err: any) {
      setProfilePicError('Profile picture upload failed. Please try again.');
    } finally {
      setIsProfilePicUploading(false);
    }
  };

  const handleProfilePicFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await handleProfilePicUpload(file);
  };

  const handleIdProofClick = () => {
    idProofInputRef.current?.click();
  };

  const handleIdProofUpload = async (file: File) => {
    // Validate file
    if (!file.type.includes('pdf') && !file.type.includes('image/')) {
      setIdProofError('Please select a valid PDF or image file.');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      setIdProofError('File size should be less than 10MB.');
      return;
    }

    setIdProofError(null);
    setIsIdProofUploading(true);
    setIdProofUploadProgress(0);
    setIdProofSuccess(false);

    try {
      // Process file with compression if it's an image
      let processedFile = file;
      let contentType = file.type;
      
      if (file.type.includes('image/')) {
        // Apply banner management compression for images
        processedFile = await imageCompression(file, {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
          onProgress: (progress) => setIdProofUploadProgress(progress)
        });
        contentType = processedFile.type;
      }

      // Generate file name and context
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const ext = contentType.includes('pdf') ? 'pdf' : contentType.split('/')[1] || 'jpg';
      const fileName = `id_proof_${Date.now()}.${ext}`;
      const context = `my-club/id-proof/${dd}-${mm}-${yyyy}`;

      // Get presigned URL
      const presignRes = await secureApi.post(
        `/api/v1/general/presigned-url`,
        {
          fileName,
          contentType: contentType,
          context,
        }
      );

      const presignedUrl = presignRes.data?.presignedUrl;
      const s3Key = presignRes.data?.key;
      
      if (!presignedUrl || !s3Key) {
        throw new Error('Failed to get presigned URL');
      }

      // Upload to S3
      const uploadRes = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: processedFile,
      });
      
      if (!uploadRes.ok) {
        throw new Error('Failed to upload file to S3');
      }

      // Update form data with new document
      const newDocument = {
        fileName: fileName,
        key: s3Key,
        type: 'id-proof' as const
      };

      setFormData(prev => ({
        ...prev,
        documents: [...prev.documents.filter(doc => doc.type !== 'id-proof'), newDocument]
      }));
      
      // Note: Change detection will automatically work since formData is updated
      
      setIdProofSuccess(true);
      
    } catch (err: any) {
      setIdProofError('ID Proof upload failed. Please try again.');
    } finally {
      setIsIdProofUploading(false);
    }
  };

  const handleIdProofFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await handleIdProofUpload(file);
  };

  const handleConsentFormClick = () => {
    consentFormInputRef.current?.click();
  };

  const handleConsentFormUpload = async (file: File) => {
    // Validate file
    if (!file.type.includes('pdf') && !file.type.includes('image/')) {
      setConsentFormError('Please select a valid PDF or image file.');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      setConsentFormError('File size should be less than 10MB.');
      return;
    }

    setConsentFormError(null);
    setIsConsentFormUploading(true);
    setConsentFormUploadProgress(0);
    setConsentFormSuccess(false);

    try {
      // Process file with compression if it's an image (for future flexibility)
      let processedFile = file;
      let contentType = file.type;
      
      if (file.type.includes('image/')) {
        // Apply banner management compression for images
        processedFile = await imageCompression(file, {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
          onProgress: (progress) => setConsentFormUploadProgress(progress)
        });
        contentType = processedFile.type;
      }

      // Generate file name and context
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const ext = contentType.includes('pdf') ? 'pdf' : contentType.split('/')[1] || 'jpg';
      const fileName = `consent_form_${Date.now()}.${ext}`;
      const context = `my-club/consent-form/${dd}-${mm}-${yyyy}`;

      // Get presigned URL
      const presignRes = await secureApi.post(
        `/api/v1/general/presigned-url`,
        {
          fileName,
          contentType: contentType,
          context,
        }
      );

      const presignedUrl = presignRes.data?.presignedUrl;
      const s3Key = presignRes.data?.key;
      
      if (!presignedUrl || !s3Key) {
        throw new Error('Failed to get presigned URL');
      }

      // Upload to S3
      const uploadRes = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: processedFile,
      });
      
      if (!uploadRes.ok) {
        throw new Error('Failed to upload file to S3');
      }

      // Update form data with new document
      const newDocument = {
        fileName: fileName,
        key: s3Key,
        type: 'consent-form' as const
      };

      setFormData(prev => ({
        ...prev,
        documents: [...prev.documents.filter(doc => doc.type !== 'consent-form'), newDocument]
      }));
      
      // Note: Change detection will automatically work since formData is updated
      
      setConsentFormSuccess(true);
      
    } catch (err: any) {
      setConsentFormError('Consent form upload failed. Please try again.');
    } finally {
      setIsConsentFormUploading(false);
    }
  };

  const handleConsentFormFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await handleConsentFormUpload(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const getProfilePicUrl = (profilePic: string) => {
    if (!profilePic) return null;
    return getImageUrl(profilePic);
  };

    const getDocumentUrl = (documentKey: string) => {
    if (!documentKey) return '';
    return getImageUrl(documentKey);
  };

  // Function to check if form data has changed from original
  const hasFormChanged = () => {
    if (!player) return true; // For new players, always allow save
    
    return (
      formData.name !== originalFormData.name ||
      formData.parentName !== originalFormData.parentName ||
      formData.profilePic !== originalFormData.profilePic ||
      formData.dob !== originalFormData.dob ||
      formData.ageCategory !== originalFormData.ageCategory ||
      JSON.stringify(formData.documents) !== JSON.stringify(originalFormData.documents)
    );
  };

  // Function to check if all required fields are filled and valid
  const isFormValid = (): boolean => {
    // Check if name is filled and valid
    if (!formData.name.trim() || formData.name.trim().length < 2) {
      return false;
    }
    
    // Check if parent name is filled and valid
    if (!formData.parentName.trim() || formData.parentName.trim().length < 2) {
      return false;
    }
    
    // Check if date of birth is filled and valid
    if (!formData.dob) {
      return false;
    }
    
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(formData.dob)) {
      return false;
    }
    
    const date = new Date(formData.dob);
    if (isNaN(date.getTime()) || date > new Date()) {
      return false;
    }
    
    // Check if age category is selected
    if (!formData.ageCategory) {
      // If age category is empty but we have teamAgeCategories, try to auto-fill
      if (teamAgeCategories && teamAgeCategories.length > 0) {
        setFormData(prev => ({ ...prev, ageCategory: teamAgeCategories[0] }));
        return false; // Return false this time, but it will be set for next validation
      }
      return false;
    }
    
    // MANDATORY: Check if profile picture is uploaded
    if (!formData.profilePic || formData.profilePic.trim() === '') {
      return false;
    }
    
    // MANDATORY: Check if both ID proof and consent form are uploaded (for all contexts now)
    const hasIdProof = formData.documents.some(doc => doc.type === 'id-proof');
    const hasConsentForm = formData.documents.some(doc => doc.type === 'consent-form');
    
    if (!hasIdProof || !hasConsentForm) {
      return false;
    }
    
    return true;
  };

  // Add CSS for disabled state
  const disabledStyles = `
    .document-upload-btn.disabled,
    .player-profile-pic.disabled {
      opacity: 0.6;
      cursor: not-allowed;
      pointer-events: none;
    }
    
    .document-upload-btn.disabled:hover,
    .player-profile-pic.disabled:hover {
      background-color: #f8f9fa;
      border-color: #dee2e6;
    }
  `;

  return (
    <>
      <style>{disabledStyles}</style>
      <Form onSubmit={handleSubmit} className="player-form-section">
      {/* Read-Only Status Indicator */}
      {isReadOnly && player && player.verificationStatus === 'approved' && (
        <div className="alert alert-info mb-3" role="alert">
          <i className="ri-information-line me-2"></i>
          <strong>Approved Player - View Only</strong>
          <br />
          <small>This player has been approved and cannot be modified.</small>
        </div>
      )}
      
      {player && player.verificationStatus === 'referred_back' && (
        <div className="alert alert-warning mb-3" role="alert">
          <i className="ri-arrow-go-back-line me-2"></i>
          <strong>Referred Back Player</strong>
          <br />
          <small>
            This player has been referred back with remarks: {player.remark || 'No remarks provided'}
          </small>
        </div>
      )}
      
      <Row>
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Profile Picture<span className="text-danger">*</span></Form.Label>
            <div className="player-profile-pic-upload-section">
              <div className="player-profile-pic-upload">
                <div 
                  className={`profile-pic-preview ${isReadOnly ? 'disabled' : ''}`}
                  style={{
                    background: getProfilePicUrl(formData.profilePic) ? `url(${getProfilePicUrl(formData.profilePic)}) center/cover` : '#f8f9fa'
                  }}
                  onClick={isReadOnly ? undefined : handleProfilePicClick}
                >
                  {!getProfilePicUrl(formData.profilePic) && (
                    <div className="upload-placeholder">
                      <i className="ri-user-line"></i>
                      <div className="small">Upload Profile Picture</div>
                    </div>
                  )}
                  {isProfilePicUploading && (
                    <div className="upload-overlay">
                      <div className="spinner-border spinner-border-sm text-light" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <div className="small mt-2 text-light">Uploading... {Math.round(profilePicUploadProgress)}%</div>
                    </div>
                  )}
                </div>
                {getProfilePicUrl(formData.profilePic) && !isReadOnly && (
                  <div 
                    className="edit-icon"
                    onClick={handleProfilePicClick}
                  >
                    <i className="ri-pencil-line"></i>
                  </div>
                )}
                <input
                  type="file"
                  ref={profilePicInputRef}
                  accept="image/*"
                  onChange={handleProfilePicFileChange}
                  style={{ display: 'none' }}
                />
              </div>
              
              {/* Profile pic upload error and success */}
              {profilePicError && (
                <div className="document-upload-error">
                  <i className="ri-error-warning-line"></i>
                  {profilePicError}
                </div>
              )}
              
              {profilePicSuccess && (
                <div className="document-upload-success">
                  <i className="ri-check-line"></i>
                  Profile picture uploaded successfully!
                </div>
              )}
              
              {/* Profile pic validation error */}
              {validationErrors.profilePic && (
                <div className="document-upload-error">
                  <i className="ri-error-warning-line"></i>
                  {validationErrors.profilePic}
                </div>
              )}
            </div>
          </Form.Group>
        </Col>
      </Row>
      
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Player Name<span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              placeholder="Enter player name"
              isInvalid={!!validationErrors.name}
              readOnly={isReadOnly}
              disabled={isReadOnly}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.name}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Name of Parent<span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              value={formData.parentName}
              onChange={(e) => handleFormChange('parentName', e.target.value)}
              placeholder="Enter parent name"
              isInvalid={!!validationErrors.parentName}
              readOnly={isReadOnly}
              disabled={isReadOnly}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.parentName}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        {showTeamField ? (
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Team Name<span className="text-danger">*</span></Form.Label>
              <Select
                classNamePrefix="react-select"
                value={teams.find(team => team.value === teamId) || null}
                onChange={(selectedOption) => {
                  if (selectedOption && onTeamChange) {
                    onTeamChange(selectedOption.value);
                    // Auto-set age category from selected team
                    const selectedTeam = teams.find(team => team.value === selectedOption.value);
                    if (selectedTeam && selectedTeam.ageCategory) {
                      handleFormChange('ageCategory', selectedTeam.ageCategory);
                    }
                  }
                }}
                options={teams}
                placeholder="Select team"
                isDisabled={isReadOnly || !onTeamChange || !!(player && !isReadOnly)} // Disabled if read-only, no team change callback, or editing existing player
                styles={{
                  menu: (base) => ({ ...base, zIndex: 9999 })
                }}
              />
            </Form.Group>
          </Col>
        ) : (
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Date of Birth<span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="date"
                value={formData.dob}
                onChange={(e) => handleFormChange('dob', e.target.value)}
                onClick={(e) => {
                  // Programmatically open the date picker when clicking anywhere in the field
                  const input = e.target as HTMLInputElement;
                  input.showPicker?.();
                }}
                isInvalid={!!validationErrors.dob}
                readOnly={isReadOnly}
                disabled={isReadOnly}
                style={{ cursor: 'pointer' }}
                {...getDobDateLimits()} // Apply min/max date constraints
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.dob}
              </Form.Control.Feedback>
              {isTournamentLoading && (
                <div className="text-muted small mt-1">
                  <i className="ri-loader-4-line spin me-1"></i>
                  Loading tournament data...
                </div>
              )}
              {!isTournamentLoading && dobValidationMessage && (
                <div className="text-info small mt-1">
                  <i className="ri-information-line me-1"></i>
                  {dobValidationMessage}
                </div>
              )}
              {!isTournamentLoading && !dobValidationMessage && tournamentData && formData.ageCategory && (
                <div className="text-muted small mt-1">
                  <i className="ri-information-line me-1"></i>
                  No DOB restrictions for {formData.ageCategory}
                </div>
              )}
              {!isTournamentLoading && !tournamentData && (
                <div className="text-muted small mt-1">
                  <i className="ri-information-line me-1"></i>
                  Tournament data not available - no DOB restrictions
                </div>
              )}
            </Form.Group>
          </Col>
        )}
      </Row>
      
      {showTeamField && (
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Date of Birth<span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="date"
                value={formData.dob}
                onChange={(e) => handleFormChange('dob', e.target.value)}
                onClick={(e) => {
                  // Programmatically open the date picker when clicking anywhere in the field
                  const input = e.target as HTMLInputElement;
                  input.showPicker?.();
                }}
                isInvalid={!!validationErrors.dob}
                readOnly={isReadOnly}
                disabled={isReadOnly}
                style={{ cursor: 'pointer' }}
                {...getDobDateLimits()} // Apply min/max date constraints
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.dob}
              </Form.Control.Feedback>
              {isTournamentLoading && (
                <div className="text-muted small mt-1">
                  <i className="ri-loader-4-line spin me-1"></i>
                  Loading tournament data...
                </div>
              )}
              {!isTournamentLoading && dobValidationMessage && (
                <div className="text-info small mt-1">
                  <i className="ri-information-line me-1"></i>
                  {dobValidationMessage}
                </div>
              )}
              {!isTournamentLoading && !dobValidationMessage && tournamentData && formData.ageCategory && (
                <div className="text-muted small mt-1">
                  <i className="ri-information-line me-1"></i>
                  No DOB restrictions for {formData.ageCategory}
                </div>
              )}
              {!isTournamentLoading && !tournamentData && (
                <div className="text-muted small mt-1">
                  <i className="ri-information-line me-1"></i>
                  Tournament data not available - no DOB restrictions
                </div>
              )}
            </Form.Group>
          </Col>
          {!hideAgeCategoryField && !player ? (
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Age Category<span className="text-danger">*</span></Form.Label>
                <Select
                  classNamePrefix="react-select"
                  value={formData.ageCategory ? { value: formData.ageCategory, label: formData.ageCategory } : null}
                  onChange={(selectedOption) => {
                    handleFormChange('ageCategory', selectedOption ? selectedOption.value : '');
                  }}
                  options={ageCategories.map(category => ({ value: category, label: category }))}
                  placeholder="Select age category"
                  isDisabled={isReadOnly}
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                    menu: (base) => ({ ...base, zIndex: 99999 })
                  }}
                />
                {validationErrors.ageCategory && (
                  <div className="invalid-feedback d-block">
                    {validationErrors.ageCategory}
                  </div>
                )}
              </Form.Group>
            </Col>
          ) : (
            <Col md={6}>
              {/* Empty column for layout balance */}
            </Col>
          )}
        </Row>
      )}
      
             {/* Hidden input for age category when not showing team field */}
       {!showTeamField && (
         <input 
           type="hidden" 
           value={formData.ageCategory} 
           onChange={() => {}} 
         />
       )}
       
       {/* Hidden input for age category when team field is shown but age category field is hidden */}
       {showTeamField && (hideAgeCategoryField || player) && (
         <input 
           type="hidden" 
           value={formData.ageCategory} 
           onChange={() => {}} 
         />
       )}

      {/* Document Upload Section */}
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>ID Proof<span className="text-danger">*</span></Form.Label>
            <div className="document-upload-section">
              <div className={`document-upload-btn ${isReadOnly ? 'disabled' : ''}`} onClick={isReadOnly ? undefined : handleIdProofClick}>
                {(() => {
                  const idProofDoc = formData.documents.find(doc => doc.type === 'id-proof');
                  console.log('Rendering ID Proof - formData.documents:', formData.documents);
                  console.log('ID Proof document found:', idProofDoc);
                  return idProofDoc;
                })() ? (
                  <div className="document-uploaded">
                    <i className="ri-file-text-line"></i>
                    <div className="document-info">
                      <span>{formData.documents.find(doc => doc.type === 'id-proof')?.fileName}</span>
                    </div>
                    <Button
                      variant="link"
                      size="sm"
                      className="view-document-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        const idProofDoc = formData.documents.find(doc => doc.type === 'id-proof');
                        if (idProofDoc) {
                          window.open(getDocumentUrl(idProofDoc.key), '_blank');
                        }
                      }}
                    >
                      <i className="ri-eye-line"></i>
                    </Button>
                  </div>
                ) : (
                  <div className="document-upload-placeholder">
                    <i className="ri-upload-line"></i>
                    <span>Upload ID Proof</span>
                    <small>PDF or Image (max 10MB)</small>
                  </div>
                )}
              </div>
              <input
                type="file"
                ref={idProofInputRef}
                accept=".pdf,image/*"
                onChange={handleIdProofFileChange}
                style={{ display: 'none' }}
              />
              
              {/* ID Proof upload progress and error */}
              {isIdProofUploading && (
                <div className="document-upload-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${idProofUploadProgress}%` }}
                    ></div>
                  </div>
                  <span>Uploading... {Math.round(idProofUploadProgress)}%</span>
                </div>
              )}
              
              {idProofError && (
                <div className="document-upload-error">
                  <i className="ri-error-warning-line"></i>
                  {idProofError}
                </div>
              )}
              
              {idProofSuccess && (
                <div className="document-upload-success">
                  <i className="ri-check-line"></i>
                  ID Proof uploaded successfully!
                </div>
              )}
              
              {/* ID Proof validation error */}
              {validationErrors.documents && !formData.documents.some(doc => doc.type === 'id-proof') && (
                <div className="document-upload-error">
                  <i className="ri-error-warning-line"></i>
                  ID Proof is required
                </div>
              )}
            </div>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Consent Form<span className="text-danger">*</span></Form.Label>
            <div className="document-upload-section">
              <div className={`document-upload-btn ${isReadOnly ? 'disabled' : ''}`} onClick={isReadOnly ? undefined : handleConsentFormClick}>
                {(() => {
                  const consentFormDoc = formData.documents.find(doc => doc.type === 'consent-form');
                  console.log('Rendering Consent Form - formData.documents:', formData.documents);
                  console.log('Consent Form document found:', consentFormDoc);
                  return consentFormDoc;
                })() ? (
                  <div className="document-uploaded">
                    <i className="ri-file-text-line"></i>
                    <div className="document-info">
                      <span>{formData.documents.find(doc => doc.type === 'consent-form')?.fileName}</span>
                    </div>
                    <Button
                      variant="link"
                      size="sm"
                      className="view-document-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        const consentFormDoc = formData.documents.find(doc => doc.type === 'consent-form');
                        if (consentFormDoc) {
                          window.open(getDocumentUrl(consentFormDoc.key), '_blank');
                        }
                      }}
                    >
                      <i className="ri-eye-line"></i>
                    </Button>
                  </div>
                ) : (
                  <div className="document-upload-placeholder">
                    <i className="ri-upload-line"></i>
                    <span>Upload Consent Form</span>
                    <small>PDF or Image (max 10MB)</small>
                  </div>
                )}
              </div>
              <input
                type="file"
                ref={consentFormInputRef}
                accept=".pdf,image/*"
                onChange={handleConsentFormFileChange}
                style={{ display: 'none' }}
              />
              
              {/* Consent Form upload progress and error */}
              {isConsentFormUploading && (
                <div className="document-upload-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${consentFormUploadProgress}%` }}
                    ></div>
                  </div>
                  <span>Uploading... {Math.round(consentFormUploadProgress)}%</span>
                </div>
              )}
              
              {consentFormError && (
                <div className="document-upload-error">
                  <i className="ri-error-warning-line"></i>
                  {consentFormError}
                </div>
              )}
              
              {consentFormSuccess && (
                <div className="document-upload-success">
                  <i className="ri-check-line"></i>
                  Consent Form uploaded successfully!
                </div>
              )}
              
              {/* Consent Form validation error */}
              {validationErrors.documents && !formData.documents.some(doc => doc.type === 'consent-form') && (
                <div className="document-upload-error">
                  <i className="ri-error-warning-line"></i>
                  Consent Form is required
                </div>
              )}
              
              {/* Download sample consent form link */}
              <div className="sample-consent-form-link mt-2">
                <a 
                  href={getImageUrl('shared/web-admin/documents/SFL+Football+League+-+Parent+consent+form.pdf')} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary text-decoration-none"
                >
                  <i className="ri-download-line me-1"></i>
                  Download sample consent form
                </a>
              </div>
            </div>
          </Form.Group>
        </Col>
      </Row>
      
      {submitError && (
        <Alert variant="danger" className="mb-3">
          <i className="ri-error-warning-line me-2"></i>
          {submitError}
        </Alert>
      )}
      
      {/* Action Buttons - Only show when not in read-only mode */}
      {!isReadOnly && (
        <div className="d-flex justify-content-end">
          <Button 
            variant="secondary" 
            onClick={handleCancel} 
            className="me-2" 
            disabled={isSubmitting}
          >
            Cancel
          </Button>
                  <Button 
          variant="primary" 
          type="submit"
          disabled={isSubmitting || isProfilePicUploading || !hasFormChanged() || !isFormValid()}
        >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {player ? 'Updating...' : 'Adding...'}
              </>
            ) : (
              player ? 'Update Player' : 'Add Player'
            )}
          </Button>
        </div>
      )}
    </Form>
    </>
  );
};

export default PlayerForm; 